/**
 * What the sign-up screens call (screenshots/93–97), wired to Better Auth (worker/src/betterauth.js):
 *   email link   POST /api/auth/sign-in/magic-link            magicLink plugin (the link opens /dashboard/signin?token=)
 *   6-digit code POST /api/auth/email-otp/send-verification-otp, /api/auth/sign-in/email-otp   emailOTP plugin
 *   passkey      POST /api/auth/passkey/*                      passkey plugin: needs the `@better-auth/passkey` package,
 *                                                              which isn't installed; until then passkey() reports it
 *   invite       GET  /api/pass/invite/<id> (worker/src/pass-api.js), then organization.acceptInvitation after sign-in
 * No passwords anywhere.
 */
import { authClient } from "./authClient";
import type { Invite } from "./signupFlow";
import { trackSignIn } from "../lib/analytics";

type R = Promise<{ error?: { message: string; status?: number } | null }>;
export interface AuthApi {
  sendLink: (a: { email: string; name?: string; callbackURL: string }) => R;
  sendCode: (email: string) => R;
  verifyCode: (email: string, code: string, name?: string) => R;
  passkey: () => R;
}

const err = (e: { message?: string; status?: number } | null | undefined) =>
  e ? { error: { message: e.status === 429 ? "Too many requests. Try again in a few minutes." : e.message || "Something went wrong. Try again.", status: e.status } } : {};

/** Passkey sign-in, once the passkey plugin is installed on both sides (it adds signIn.passkey to the client). */
const passkeySignIn = (authClient as unknown as { signIn: { passkey?: () => Promise<{ error?: { message?: string } | null }> } }).signIn.passkey;

export const betterAuthApi: AuthApi = {
  sendLink: async ({ email, name, callbackURL }) => err((await authClient.signIn.magicLink({ email, name, callbackURL })).error),
  sendCode: async (email) => err((await authClient.emailOtp.sendVerificationOtp({ email, type: "sign-in" })).error),
  verifyCode: async (email, otp, name) => {
    const r = await authClient.signIn.emailOtp({ email, otp, ...(name ? { name } : {}) });
    if (!r.error) trackSignIn("email_code", (r.data as { user?: { createdAt?: string | Date } } | null)?.user);
    return err(r.error);
  },
  passkey: async () => {
    if (!passkeySignIn) return { error: { message: "Passkeys aren’t available yet." } };
    const r = await passkeySignIn();
    if (!r.error) trackSignIn("passkey", null);
    return err(r.error);
  },
};

/** The invitation behind /dashboard/invite/<id>, readable before sign-in (pending and unexpired only). */
export async function loadInvite(id: string): Promise<Invite | null> {
  const r = await fetch(`/api/pass/invite/${encodeURIComponent(id)}`, { credentials: "include" }).catch(() => null);
  return r?.ok ? ((await r.json()) as Invite) : null;
}

/** After signing in from an invite: join the team (Better Auth checks the invite's email matches the session). */
export async function acceptInvite(id: string): Promise<boolean> {
  return (await acceptInviteTo(id)) !== null;
}

/** Same, answering the team joined (its organization id; "" when unknown), or null when the invite didn't work. */
export async function acceptInviteTo(id: string): Promise<string | null> {
  const r = await authClient.organization.acceptInvitation({ invitationId: id });
  if (r.error) return null;
  return (r.data as { invitation?: { organizationId?: string } } | null)?.invitation?.organizationId ?? "";
}

/** The emailed link lands on /dashboard/signin?token=…&next=…: verify it here, then go on. */
export async function verifyLink(token: string): Promise<{ ok: boolean; twoFactor?: boolean; message?: string }> {
  const r = await authClient.magicLink.verify({ query: { token } });
  const data = r.data as { twoFactorRedirect?: boolean } | null;
  if (data?.twoFactorRedirect) return { ok: false, twoFactor: true };
  if (!r.error) trackSignIn("email_link", (r.data as { user?: { createdAt?: string | Date } } | null)?.user);
  return r.error ? { ok: false, message: r.error.message } : { ok: true };
}
